import "./chartComp.less";

import React from "react";
import { observer } from "mobx-react";
import {
  Area,
  Bar,
  Brush,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Chart, { ColumnInfoDto } from "core/grid/domain/chart";
import gridService from "core/grid/gridService";
import Grid from "core/grid/domain/grid";
import { observable } from "mobx";
import {
  ChartConfigDto,
  ChartType,
  COLORS,
  DataSeries,
  PaneChartConfig,
} from "./chartDomain";
import { SplitPane } from "../splitPane/splitPane";

import Axios, { AxiosResponse } from "axios";
import { ConfigChangeTypeEnum, ConfigPane } from "./configPane";
import { DATATYPE } from "core/metamodel/mdField";
import { formatDate, formatPeriod } from "core/common/utils/dateUtils";
import { CONFIGLEVEL } from "core/metamodel/metaConstant";
import moment from "moment";

interface ChartCompProps {
  chart: Chart;
  grid: Grid;
  onCustomDataFetch: (columnNames: string[]) => void;
}

@observer
export class ChartComp extends React.Component<ChartCompProps, undefined> {
  initialDataSeries: DataSeries[] = [];
  @observable currentDataSeries: DataSeries[] = [];
  @observable allProperitiesSelected: boolean = false;
  @observable currentChartConfigs: Map<number, PaneChartConfig> = new Map();
  @observable initChartConfigs: Map<number, PaneChartConfig> = new Map();
  @observable activeChartConfigId: number;
  @observable chartConfigsLoaded = false;
  @observable dataSeriesExtracted = false;
  @observable.shallow sortedData?: any[];

  shouldUpdateDataSeriesFromConfig = true;
  newChartId: number = -1;
  customDataFetchedIds: Set<string> = new Set();

  getNewChart(): PaneChartConfig {
    return {
      chartId: this.newChartId,
      chartName: "New chart",
      chartType: ChartType.BAR,
      isNew: true,
      chartColumnsIds: [],
      configLevel: CONFIGLEVEL.USER,
      chartSorted: false,
    };
  }

  componentWillMount() {
    let { chart, grid } = this.props;
    this.extractDataSeries(chart.data, chart.columnsInfo);

    Axios.get(
      `/api/core/grid/${grid.activeGridConfig.gridId}/chart/config`
    ).then((response: AxiosResponse) => {
      let chartConfigDtos: ChartConfigDto[] = response.data;

      if (chartConfigDtos.length !== 0) {
        chartConfigDtos.sort((a: ChartConfigDto, b: ChartConfigDto) =>
          ("" + a.chartName).localeCompare(b.chartName)
        );
        //fill chart configs
        this.activeChartConfigId = chartConfigDtos[0].chartId;
        chartConfigDtos.forEach((cC) => {
          this.currentChartConfigs.set(cC.chartId, cC);
          this.initChartConfigs.set(cC.chartId, cC);
        });
      } else {
        this.onNewChart(true); //handle no config, create new chart
      }
      const activeConfig = this.initChartConfigs.get(this.activeChartConfigId);
      this.xAxisDataSort(activeConfig.chartSorted);
      this.chartConfigsLoaded = true;
    });
  }

  onNewChart(isInitial: boolean = false) {
    //isInitial = no configs saved in past
    this.currentChartConfigs.set(this.newChartId, this.getNewChart());
    this.initChartConfigs.set(this.newChartId, this.getNewChart());
    this.activeChartConfigId = this.newChartId;

    if (!isInitial) {
      this.initialDataSeries.forEach((dS) => (dS.enable = false));
      this.currentDataSeries.forEach((dS) => (dS.enable = false));
    }
  }

  onDeleteChart() {
    let deletedChartId = this.activeChartConfigId;

    if (this.initChartConfigs.size === 1) {
      this.onNewChart();
    } else {
      this.initChartConfigs.forEach((cC) => {
        if (cC.chartId !== deletedChartId) {
          this.activeChartConfigId = cC.chartId;
          return;
        }
      });
    }

    this.initChartConfigs.delete(deletedChartId);
    this.currentChartConfigs.delete(deletedChartId);

    this.updateSeriesFromConfig();
  }

  updateSeriesFromConfig(forceUpdate: boolean = false) {
    if (
      forceUpdate ||
      (this.shouldUpdateDataSeriesFromConfig &&
        this.chartConfigsLoaded &&
        this.dataSeriesExtracted)
    ) {
      let enabledMap: Map<string, boolean> = new Map();
      let activeConfig = this.currentChartConfigs.get(this.activeChartConfigId);

      let enabledColumnIds: string[] = activeConfig.chartColumnsIds;
      this.props.chart.columnsInfo.forEach((column: ColumnInfoDto) => {
        //fill enabled map
        enabledMap.set(
          column.id,
          enabledColumnIds.find((c) => c === column.id) !== undefined
        );
      });

      this.initialDataSeries.forEach((dS) => {
        dS.enable = enabledMap.get(dS.id);
        dS.type = activeConfig.chartType;
      });

      let columnIdsToFetch: string[] = [];

      this.currentDataSeries.forEach((dS) => {
        dS.enable = enabledMap.get(dS.id);
        dS.type = activeConfig.chartType;

        if (
          dS.enable &&
          !dS.isUsedOnGrid &&
          !this.customDataAlreadyFetched(dS.id)
        ) {
          //add others columns ids to fetch
          columnIdsToFetch.push(dS.id);
          this.customDataFetchedIds.add(dS.id);
        }

        if (
          activeConfig.chartXField === dS.id &&
          !this.customDataAlreadyFetched(dS.id)
        ) {
          //x axis id to fetch
          columnIdsToFetch.push(dS.id);
          this.customDataFetchedIds.add(dS.id);
        }
      });

      if (columnIdsToFetch.length > 0) {
        this.props.onCustomDataFetch(columnIdsToFetch);
      }

      if (!forceUpdate) this.shouldUpdateDataSeriesFromConfig = false;
    }
  }

  componentWillUpdate() {
    this.updateSeriesFromConfig();
  }

  componentDidUpdate(
    prevProps: Readonly<ChartCompProps>,
    _: Readonly<undefined>,
    __: any
  ) {
    if (prevProps.chart != this.props.chart) {
      let { chart } = this.props;
      this.extractDataSeries(chart.data, chart.columnsInfo);
      this.updateSeriesFromConfig(true);
    }
  }

  formatDataValues(data: any[], columns: ColumnInfoDto[]) {
    let dateLabelSet: Set<string> = new Set();
    let durationLabelSet: Set<string> = new Set();
    columns.forEach((column) => {
      if (column.dataType === DATATYPE.DATE) {
        dateLabelSet.add(column.effectiveHeaderLabel);
      } else if (column.dataType === DATATYPE.DURATION) {
        durationLabelSet.add(column.effectiveHeaderLabel);
      }
    });

    data.forEach((piece) => {
      dateLabelSet.forEach((label) => {
        if (piece[label]) {
          piece[label] = formatDate(piece[label]);
        }
      });

      durationLabelSet.forEach((label) => {
        if (piece[label]) {
          piece[label] = formatPeriod(piece[label] * 1000, true);
        }
      });
    });
  }

  extractDataSeries = (data: any[], columns: ColumnInfoDto[]) => {
    let colorIndex = 0;

    if (data == null) {
      return;
    }
    if (data.length > 0) {
      this.initialDataSeries = [];
      this.currentDataSeries = [];

      this.formatDataValues(data, columns);

      columns.forEach((column: ColumnInfoDto) => {
        this.initialDataSeries.push({
          id: column.id,
          name: column.effectiveHeaderLabel,
          enable: false,
          type: ChartType.BAR,
          color: COLORS[colorIndex],
          isUsedOnGrid: column.isUsedOnGrid,
        });

        this.currentDataSeries.push({
          id: column.id,
          name: column.effectiveHeaderLabel,
          enable: false,
          type: ChartType.BAR,
          color: COLORS[colorIndex],
          isUsedOnGrid: column.isUsedOnGrid,
        });

        colorIndex++;
        if (colorIndex >= COLORS.length) {
          colorIndex = 0;
        }
      });
    }
    this.dataSeriesExtracted = true;
  };

  renderDataSeries = () => {
    let elements = [];
    let index: number = 0;

    for (let ds of this.currentDataSeries) {
      if (ds.enable == false) {
        continue;
      }

      if (ds.type == ChartType.BAR) {
        let comp = (
          <Bar
            key={`${ds.name}-${index}`}
            dataKey={ds.name}
            fill={ds.color}
            yAxisId="left"
          />
        );
        elements.push(comp);
      } else if (ds.type == ChartType.AREA) {
        let comp = (
          <Area
            type="monotone"
            key={`${ds.name}-${index}`}
            dataKey={ds.name}
            fill={ds.color}
            yAxisId="right"
          />
        );
        elements.push(comp);
      } else if (ds.type == ChartType.LINE) {
        let comp = (
          <Line
            type="monotone"
            key={`${ds.name}-${index}`}
            dataKey={ds.name}
            fill={ds.color}
            yAxisId="right"
          />
        );
        elements.push(comp);
      }

      index++;
    }

    const chartXField: string = this.currentChartConfigs.get(
      this.activeChartConfigId
    ).chartXField;

    if (chartXField) {
      const ds: DataSeries = this.currentDataSeries.find(
        (ds: DataSeries) => ds.id === chartXField
      );
      if (ds) {
        let comp = <XAxis key={`${ds.name}-X-${index}`} dataKey={ds.name} />;
        elements.push(comp);
      }
    }

    return elements;
  };

  customDataAlreadyFetched(columnId: string): boolean {
    return this.customDataFetchedIds.has(columnId);
  }

  xAxisDataSort(sort: boolean) {
    const { chart } = this.props;
    const activeConfig = this.currentChartConfigs.get(this.activeChartConfigId);
    if (!activeConfig.chartXField) return;

    if (sort) {
      const effectiveHeaderLabel: string = this.props.chart.columnsInfo.find(
        column => column.id === activeConfig.chartXField
      ).effectiveHeaderLabel;
      const value: any = chart.data[0][effectiveHeaderLabel];
      if (value !== undefined) {
        const isDate = moment(value, moment.ISO_8601, true).isValid();
        const isNumber = typeof value == "string" && !isNaN(value) && !isNaN(parseFloat(value));
        if (isDate) {
          this.sortedData = [...chart.data].sort((a: any, b: any) => {
            return moment(a[effectiveHeaderLabel], moment.ISO_8601).diff(
              moment(b[effectiveHeaderLabel], moment.ISO_8601)
            );
          });
        } else if(isNumber) {
          this.sortedData = [...chart.data].sort((a: any, b: any) => {
            return parseFloat(a[effectiveHeaderLabel]) < parseFloat(b[effectiveHeaderLabel]) ? -1 : 1;
          });
        } else {
          this.sortedData = [...chart.data].sort((a: any, b: any) => {
            return a[effectiveHeaderLabel] < b[effectiveHeaderLabel] ? -1 : 1;
          });
        }
      }
    } else {
      this.sortedData = undefined;
    }
  }
  renderConfigPane() {
    return (
      <ConfigPane
        initChartConfigs={this.initChartConfigs}
        currentChartConfigs={this.currentChartConfigs}
        activeChartConfigId={this.activeChartConfigId}
        onChartConfigChange={(
          chartConfig: ChartConfigDto,
          changeType: ConfigChangeTypeEnum
        ) => {
          this.currentDataSeries.forEach((dS) => {
            dS.type = chartConfig.chartType;
          });
          this.currentChartConfigs.set(chartConfig.chartId, chartConfig);
          switch (changeType) {
            case ConfigChangeTypeEnum.X_AXIS:
              this.xAxisDataSort(chartConfig.chartSorted);
              this.updateSeriesFromConfig(true);
              break;
            case ConfigChangeTypeEnum.SORTED:
              this.xAxisDataSort(chartConfig.chartSorted);
              break;
          }
        }}
        onOtherColumnSelected={(columnName: string, columnId: string) => {
          if (!this.customDataAlreadyFetched(columnId)) {
            //data not fetched
            console.log(
              `Fetching custom data for chart. columnName: ${columnName}, columnId: ${columnId}`
            );
            this.customDataFetchedIds.add(columnId);
            this.props.onCustomDataFetch([columnId]);
          }
        }}
        onActiveChartConfigChange={(
          newActiveChartConfigId: number,
          reinitCurrent: boolean
        ) => {
          if (reinitCurrent) {
            this.currentChartConfigs.set(
              this.activeChartConfigId,
              this.initChartConfigs.get(this.activeChartConfigId)
            );
          }
          this.activeChartConfigId = newActiveChartConfigId;

          this.updateSeriesFromConfig(true);
        }}
        onChartConfigSave={(chartConfig: ChartConfigDto) => {
          this.initChartConfigs.set(chartConfig.chartId, chartConfig);
          this.currentChartConfigs.set(chartConfig.chartId, chartConfig);

          if (this.activeChartConfigId === this.newChartId) {
            //use id for new chart
            this.activeChartConfigId = chartConfig.chartId;
            this.initChartConfigs.delete(this.newChartId);
            this.currentChartConfigs.delete(this.newChartId);
          }

          this.updateSeriesFromConfig(true);
        }}
        onNewChart={() => this.onNewChart()}
        onDeleteChart={() => this.onDeleteChart()}
        currentDataSeries={this.currentDataSeries}
        initDataSeries={this.initialDataSeries}
        allSelected={this.allProperitiesSelected}
        onSelectAll={(allSelected: boolean) => {
          this.allProperitiesSelected = allSelected;
          this.currentDataSeries.forEach((ds) => {
            ds.enable = this.allProperitiesSelected;
          });
        }}
        entityName={this.props.chart.entityName}
        grid={this.props.grid}
      />
    );
  }

  render() {
    let { chart } = this.props;

    if (!this.chartConfigsLoaded) return null;

    let columnsData = this.renderDataSeries();
    return (
      <SplitPane>
        <ChartView
          data={this.sortedData ? this.sortedData.slice() : chart.data}
          columnsData={columnsData}
          ref="chart-view"
        />
        {this.chartConfigsLoaded && this.dataSeriesExtracted ? (
          this.renderConfigPane()
        ) : (
          <div>Loading</div>
        )}
      </SplitPane>
    );
  }
}

interface ChartProps {
  data: any;
  columnsData: any;
}

class ChartView extends React.Component<ChartProps> {
  render() {
    return (
      <div
        className="chart-container"
        style={{ width: "100%", height: "100%" }}
      >
        <ResponsiveContainer>
          <ComposedChart data={this.props.data.slice()}>
            {this.props.columnsData}
            <Legend />
            <Brush height={30} stroke="#8884d8" />
            <CartesianGrid stroke="#ccc" strokeDasharray="5 5" />
            <YAxis yAxisId="left" orientation="left" />
            <YAxis yAxisId="right" orientation="right" />
            <Tooltip />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    );
  }
}

interface ChartGridWrapperCompProps {
  grid: Grid;
}

@observer
export class ChartGridWrapperComp extends React.Component<
  ChartGridWrapperCompProps,
  undefined
> {
  @observable chart: Chart;

  fetchCustomData(columnNames: string[]) {
    gridService.chart(this.props.grid, columnNames).then((chart) => {
      for (let i = 0; i < this.chart.data.length; i++) {
        this.chart.data[i] = Object.assign(this.chart.data[i], chart.data[i]);
      }
      this.chart = Object.assign({}, this.chart);
      console.log("Custom data fetched", columnNames);
      //TODO: use deep observing of data
    });
  }

  componentDidMount(): void {
    gridService.chart(this.props.grid).then((chart) => {
      this.chart = chart;
    });
  }

  render() {
    return this.chart && this.chart.data ? (
      <ChartComp
        chart={this.chart}
        grid={this.props.grid}
        onCustomDataFetch={(columnNames: string[]) => {
          this.fetchCustomData(columnNames);
        }}
      />
    ) : null;
  }
}



// WEBPACK FOOTER //
// ./src/core/components/chart/chartComp.tsx